// Third-party
import Divider from '@mui/material/Divider';

// Components
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SvgIcon from '@mui/material/SvgIcon';
import { mdiStar, mdiStarOutline } from '@mdi/js';
import Icon from '@mdi/react';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import { BaseTooltip } from '@mgm/common/components/base';
import { SettingsWrapper } from '../../../layout';
import { SettingsHeader } from './SettingsHeader';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getAuthenticateUser, getTokenDetails } from '../../../store/authenticate/selectors';
import { ReactComponent as ShopIcon } from '../../../static/icons/ic_shop.svg';
import { ReactComponent as DoorIcon } from '../../../static/icons/ic_door.svg';
import { useGetShopAPI, useSetDefaultShopAPI } from '../../../hooks/api';

const ShopsSettings = () => {
  const { mutate: setDefaultShopMutate } = useSetDefaultShopAPI();
  const { mutate: getShopMutate } = useGetShopAPI();
  const tokenDetails = useAppSelector(getTokenDetails);
  const authenticateUser = useAppSelector(getAuthenticateUser);
  const theme = useTheme();
  const { t } = useTranslation('settings');

  const changeDefaultShop = (shop: string) => {
    setDefaultShopMutate({ shopName: shop });
  };

  const getShop = (shop: string) => {
    getShopMutate({ shopName: shop });
  };

  return (
    <SettingsWrapper>
      <SettingsHeader
        title={t('shops')}
        subtitle={t('controlAndPerformActions')}
      />
      <Divider sx={{ pt: 4, borderStyle: 'dashed' }} />
      <Grid mt={4} container spacing={3}>
        {tokenDetails?.shops.map((shop) => (
          <Grid
            key={shop}
            size={{
              md: 6, lg: 4,
            }}
          >
            <Card
              className="align-center cursor-pointer"
              component={Box}
              sx={{
                px: 2,
                width: 1,
                ...(shop === authenticateUser.shop.shopName && {
                  border: `1px solid ${theme.palette.primary.main}`,
                }),
                position: 'relative',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.24)',
                '&:hover': {
                  boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.24), 0 16px 32px -4px rgba(0, 0, 0, 0.24)',
                },
              }}
            >
              <Box width="100%" display="flex" alignItems="center" justifyContent="space-between">
                <Box height={110} display="flex" alignItems="center">
                  <SvgIcon color="primary" fontSize="large" component={ShopIcon} inheritViewBox />
                  <Typography
                    variant="body1"
                    sx={{
                      py: 3,
                      pl: 5,
                      transition: (theme) => theme.transitions.create('opacity', {
                        duration: theme.transitions.duration.shortest,
                      }),
                    }}
                  >
                    {shop}
                  </Typography>
                </Box>
                <Stack ml={2} spacing={2} py={2}>
                  <BaseTooltip tooltipText="Default shop">
                    <Box onClick={() => changeDefaultShop(shop)}>
                      <Icon
                        color={theme.palette.warning.main}
                        path={shop === tokenDetails.defaultShop ? mdiStar : mdiStarOutline}
                        size={1}
                      />
                    </Box>
                  </BaseTooltip>
                  {shop !== authenticateUser.shop.shopName && (
                  <BaseTooltip tooltipText="Enter shop">
                    <Box onClick={() => getShop(shop)}>
                      <SvgIcon color="primary" component={DoorIcon} inheritViewBox />
                    </Box>
                  </BaseTooltip>
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </SettingsWrapper>
  );
};

export {
  ShopsSettings,
};
